import { ConditionStatus } from '../model/types';
import { getNewDuplicateName } from './utils';
import {
  VHCRecordCheckBrakeItemTypes,
  VHCRecordCheckItemTypes,
  VHCRecordCheckWheelTyreItemTypes,
  VHCRecordItemGroups
} from 'api/generated';
import { useGetVhcRecordQuery } from 'features/vhc/api/get-record';
import { useSaveBreakItemMutation } from 'features/vhc/modals/record/forms/vhc-record/api/save-break';
import { useSaveDefaultItemMutation } from 'features/vhc/modals/record/forms/vhc-record/api/save-default';
import { useSaveWheelItemMutation } from 'features/vhc/modals/record/forms/vhc-record/api/save-wheels';
import { useRecordStore, VhcItem } from 'features/vhc/model';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

interface IProps {
  item: VhcItem;
}

export const useVhcSaveItem = ({ item }: IProps) => {
  const { description, type, group, formType, isNew } = item;

  const { setRecordModalIsOpen } = useVhcStore();

  const { id } = useParams();

  const { items } = useRecordStore();

  const { refetch: refetchRecord } = useGetVhcRecordQuery({ id: Number(id) }, undefined, { enabled: false });

  const { mutateAsync: saveDefaultItem, isLoading: isSavingDefaultItem } = useSaveDefaultItemMutation();
  const { mutateAsync: saveBreakItem, isLoading: isSavingBreakItem } = useSaveBreakItemMutation();
  const { mutateAsync: saveWheelItem, isLoading: isSavingWheelItem } = useSaveWheelItemMutation();

  const handleSaveDefaultItem = async () => {
    if (!id) return null;

    const newDescription = getNewDuplicateName(
      description || '',
      type as VHCRecordCheckItemTypes,
      group as VHCRecordItemGroups,
      items || []
    );
    try {
      const createdId = await saveDefaultItem({
        logId: Number(id),
        requestBody: {
          notes: '',
          conditionId: ConditionStatus.NOT_TESTED,
          imageUrls: [],
          type: type as VHCRecordCheckItemTypes,
          group: group as VHCRecordItemGroups,
          description: newDescription
        }
      });
      return createdId;
    } catch (error) {
      console.error('Creating error:', error);
    }
    return null;
  };

  const handleSaveBrakeItem = async () => {
    if (!id) return null;

    const newDescription = getNewDuplicateName(
      description || '',
      type as VHCRecordCheckBrakeItemTypes,
      group as VHCRecordItemGroups,
      items || []
    );
    try {
      const createdId = await saveBreakItem({
        logId: Number(id),
        requestBody: {
          notes: '',
          conditionId: ConditionStatus.NOT_TESTED,
          imageUrls: [],
          type: type as VHCRecordCheckBrakeItemTypes,
          group: group as VHCRecordItemGroups,
          description: newDescription
        }
      });
      return createdId;
    } catch (error) {
      console.error('Creating error:', error);
    }
    return null;
  };

  const handleSaveWheelTyreItem = async () => {
    if (!id) return null;

    const newDescription = getNewDuplicateName(
      description || '',
      type as VHCRecordCheckWheelTyreItemTypes,
      group as VHCRecordItemGroups,
      items || []
    );
    try {
      const createdId = await saveWheelItem({
        logId: Number(id),
        requestBody: {
          conditionId: ConditionStatus.NOT_TESTED,
          make: '',
          model: '',
          size: '',
          dotCode: '',
          notes: '',
          imageUrls: [],
          type: type as VHCRecordCheckWheelTyreItemTypes,
          group: group as VHCRecordItemGroups,
          description: newDescription
        }
      });
      return createdId;
    } catch (error) {
      console.error('Creating error:', error);
    }
    return null;
  };

  const handleCreateItem = async (): Promise<number | null> => {
    if (!formType) return null;

    let createdId: number | null = null;

    if (formType === 'default') {
      createdId = await handleSaveDefaultItem();
    } else if (formType === 'brake') {
      createdId = await handleSaveBrakeItem();
    } else if (formType === 'wheelTyre') {
      createdId = await handleSaveWheelTyreItem();
    }
    return createdId;
  };

  const { subscribe } = useRecordStore;
  const targetKey = `${type}_${group}`;

  const handleOpenVhcRecordModal = useCallback(async () => {
    if (!item) return;

    if (item.isNew) {
      try {
        await handleCreateItem();
        await refetchRecord();

        await new Promise<void>((resolve) => {
          const unsub = subscribe((state) => {
            const items = state.items;
            const created = items?.find((i) => `${i.type}_${i.group}` === targetKey && !i.isNew);
            if (created?.id) {
              setRecordModalIsOpen(
                {
                  data: created
                },
                true
              );
              unsub();
              resolve();
            }
          });
          setTimeout(() => {
            unsub();
            resolve();
          }, 3000);
        });
        return;
      } catch (error) {
        console.error('Creating error:', error);
        return;
      }
    }

    if (!item.id) return;

    setRecordModalIsOpen(
      {
        data: item
      },
      true
    );
  }, [item, subscribe, targetKey, handleCreateItem, refetchRecord]);

  const isSaving = isSavingDefaultItem || isSavingBreakItem || isSavingWheelItem;

  const handlePlusClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSaving || isNew) return;

    try {
      const createdId = await handleCreateItem();
      await refetchRecord();

      if (createdId) {
        await new Promise<void>((resolve) => {
          const unsub = subscribe((state) => {
            const items = state.items;
            const created = items?.find((i) => i.id === createdId);
            if (created?.id) {
              setRecordModalIsOpen(
                {
                  data: created
                },
                true
              );
              unsub();
              resolve();
            }
          });

          setTimeout(() => {
            unsub();
            resolve();
          }, 3000);
        });
      }
    } catch (error) {
      console.error('Error creating duplicate:', error);
    }
  };

  return {
    isSaving,
    handleOpenVhcRecordModal,
    handlePlusClick,
    handleCreateItem,
    handleSaveDefaultItem,
    handleSaveBrakeItem,
    handleSaveWheelTyreItem
  };
};
